/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * UatProgressRing - Circular SVG progress indicator
 * ============================================================================
 *
 * Renders a circular progress ring with a gradient arc from cyan to green.
 * Used in the UAT widget and banner to show test completion.
 *
 * ============================================================================
 */

interface UatProgressRingProps {
  percent: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
}

export function UatProgressRing({
  percent,
  size = 48,
  strokeWidth = 3,
  label,
}: UatProgressRingProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percent));
  const offset = circumference - (clamped / 100) * circumference;
  const gradientId = `uat-ring-grad-${size}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="uat-progress-ring"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      role="progressbar"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00f0ff" />
          <stop offset="100%" stopColor="#00ff88" />
        </linearGradient>
      </defs>

      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(0, 240, 255, 0.15)"
        strokeWidth={strokeWidth}
      />

      {/* Progress arc */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="uat-progress-ring__arc"
      />

      {/* Center label */}
      {label && (
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          fill="rgba(255, 255, 255, 0.9)"
          fontSize={size * 0.22}
          fontWeight={600}
          fontFamily="var(--font-body, sans-serif)"
        >
          {label}
        </text>
      )}

      <style>{`
        .uat-progress-ring__arc {
          transition: stroke-dashoffset 0.6s ease;
        }
        @media (prefers-reduced-motion: reduce) {
          .uat-progress-ring__arc {
            transition: none;
          }
        }
      `}</style>
    </svg>
  );
}
